/**
 * Pages
 */
import { ETL_INTEGRATION_TYPE, Values } from '../components/configuration/types';

const myIntegrationsScene = 'my-integrations-scene';

/**
 * Sections
 */
const sidebar = 'sidebar';

/**
 * Components
 */
const accordionItemUserSynchronization = 'user-synchronization';
const accordionItemHistoricalData = 'historical-data';
const accordionItemAdherenceData = 'adherence-data';
const accordionItemRealTimeData = 'real-time-data';
const accordionItemMediaImport = 'media-import';
const roleMappingSection = 'role-mapping';
const teamSelectorSection = 'team-selector';
const navItem = 'nav-item';
const configurationCard = 'configuration-card';
const configurationPicker = 'configuration-picker';
const navigation = 'navigation';

export const TEST_IDS = {
  /**
   * Test IDs for My Integrations scene.
   */
  myIntegrationsScene,
  buttonNewIntegration: `${myIntegrationsScene}:create-new-integration`,
  buttonSaveIntegration: `${myIntegrationsScene}:save-integration`,
  buttonCancelIntegration: `${myIntegrationsScene}:cancel-integration`,
  buttonCloseIntegration: `${myIntegrationsScene}:close-integration`,
  buttonDeleteIntegration: `${myIntegrationsScene}:delete-integration`,
  buttonAuthorizeWithWebex: `${myIntegrationsScene}:authorize-with-webex`,
  buttonConfigurationCard: (id: number) => `${myIntegrationsScene}:${configurationCard}:${id}`,
  inputConfigurationName: `${myIntegrationsScene}:configuration-name`,
  inputClientId: `${myIntegrationsScene}:client-id`,
  inputClientSecret: `${myIntegrationsScene}:client-secret`,
  inputOrganizationId: `${myIntegrationsScene}:organization-id`,
  inputApiUrl: `${myIntegrationsScene}:api-url`,
  inputAccessToken: `${myIntegrationsScene}:access-token`,
  inputRefreshToken: `${myIntegrationsScene}:refresh-token`,
  paginationIntegrations: `${myIntegrationsScene}:pagination`,
  illustratedMessageNoData: `${myIntegrationsScene}:illustrated-message-no-data`,

  pillCard: `${myIntegrationsScene}:${configurationCard}:pill`,

  /**
   * General configuration
   */
  pillConfiguration: `${myIntegrationsScene}:${sidebar}:pill`,
  noticeConfigurationWarning: `${myIntegrationsScene}:${sidebar}:notice-warning`,
  headingConfigurationTitle: `${myIntegrationsScene}:${sidebar}:configuration-title`,

  /**
   * Configuration picker
   */
  headingConfigurationPickerTitle: `${myIntegrationsScene}:${configurationPicker}:heading` as const,
  buttonConfigurationPickerCard: (integration: Values<typeof ETL_INTEGRATION_TYPE>) =>
    `${myIntegrationsScene}:${configurationPicker}:${integration}` as const,

  /**
   * Sidebar
   */
  headingSidebarTitle: `${myIntegrationsScene}:${sidebar}:title`,
  flexExpandedSidebar: `${myIntegrationsScene}:${sidebar}:expanded-sidebar`,

  /**
   * User Synchronization task
   */
  accordionItemUserSynchronization: `${accordionItemUserSynchronization}:accordion-item`,
  buttonToggleTeamSelector: `${accordionItemUserSynchronization}:toggle-team-selector`,
  buttonToggleRoleMapping: `${accordionItemUserSynchronization}:toggle-role-mapping`,
  buttonShrinkRoleMapping: `${accordionItemUserSynchronization}:${roleMappingSection}:shrink-role-mapping`,
  checkboxShowSelectedTeamsOnlyEnabled: `${accordionItemUserSynchronization}:show-selected-teams-only`,
  checkboxUserSynchronizationEnabled: `${accordionItemUserSynchronization}:enabled`,
  headerDeactivatedRoleMapping: `${accordionItemUserSynchronization}:${roleMappingSection}:header-deactivated-role-mapping`,
  headerTeamSelector: `${accordionItemUserSynchronization}:${roleMappingSection}:header-team-selector`,
  headerRoleMapping: `${accordionItemUserSynchronization}:${roleMappingSection}:header-role-mapping`,
  inputCronExpression: `${accordionItemUserSynchronization}:cron-expression`,
  noticeDeactivatedRoleMapping: `${accordionItemUserSynchronization}:${roleMappingSection}:notice-deactivated-role-mapping`,
  selectEleveoRole: (index: number) => `${accordionItemUserSynchronization}:${roleMappingSection}:eleveo-role:${index}`,
  selectAllTeamsButton: `${accordionItemUserSynchronization}:${teamSelectorSection}:select-all-teams-button`,
  clearTeamsButton: `${accordionItemUserSynchronization}:${teamSelectorSection}:clear-teams-button`,
  selectIntegrationTeamsSearchBar: `${accordionItemUserSynchronization}:${teamSelectorSection}:integration-teams-search-bar`,
  selectIntegrationTeams: `${accordionItemUserSynchronization}:${teamSelectorSection}:integration-teams`,
  selectGroupSupervisors: `${accordionItemUserSynchronization}:${roleMappingSection}:group-supervisor`,
  buttonDeleteDeactivatedEleveoRole: (index: number) =>
    `${accordionItemUserSynchronization}:${roleMappingSection}:delete-deactivated-eleveo-role:${index}`,
  deactivatedEleveoRole: (index: number) =>
    `${accordionItemUserSynchronization}:${roleMappingSection}:deactivated-eleveo-role:${index}`,

  noticeTeamSelectorMissingIntegrationPermission: `${myIntegrationsScene}:${accordionItemUserSynchronization}:missing-integration-teams-permission`,
  noticeTeamSelectorMissingIntegrationTeams: `${myIntegrationsScene}:${accordionItemUserSynchronization}:missing-integration-teams`,
  noticeTeamSelectorIntegrationTeamsFailed: `${myIntegrationsScene}:${accordionItemUserSynchronization}:integration-teams-failed`,
  noticeTeamSelectorIntegrationTeamsMissingIntegrationData: `${myIntegrationsScene}:${accordionItemUserSynchronization}:integration-teams-missing-integration-data`,
  noticeRoleMappingInfo: `${myIntegrationsScene}:${accordionItemUserSynchronization}:role-mapping-info`,
  noticeRoleMappingEleveoRolesFailed: `${myIntegrationsScene}:${accordionItemUserSynchronization}:eleveo-roles-failed`,
  noticeRoleMappingIntegrationRolesFailed: `${myIntegrationsScene}:${accordionItemUserSynchronization}:integration-roles-failed`,
  noticeRoleMappingMissingEleveoPermission: `${myIntegrationsScene}:${accordionItemUserSynchronization}:missing-eleveo-permission`,
  noticeRoleMappingMissingIntegrationPermission: `${myIntegrationsScene}:${accordionItemUserSynchronization}:missing-integration-permission`,
  noticeRoleMappingMissingEleveoRoles: `${myIntegrationsScene}:${accordionItemUserSynchronization}:missing-eleveo-roles`,
  noticeRoleMappingMissingIntegrationRoles: `${myIntegrationsScene}:${accordionItemUserSynchronization}:missing-integration-roles`,

  /**
   * Historical Data task
   */
  accordionItemHistoricalData: `${accordionItemHistoricalData}:accordion-item`,
  checkboxHistoricalDataEnabled: `${accordionItemHistoricalData}:enabled`,
  inputCronExpressionHistoricalData: `${accordionItemHistoricalData}:cron-expression`,
  inputLookBackPeriodHistoricalData: `${accordionItemHistoricalData}:look-back-period`,
  noticeHistoricalLookBackWarning: `${myIntegrationsScene}:${accordionItemHistoricalData}:notice-look-back-warning`,

  /**
   * Adherence task
   */
  accordionItemAdherenceData: `${accordionItemAdherenceData}:accordion-item`,
  checkboxAdherenceDataEnabled: `${accordionItemAdherenceData}:enabled`,
  inputCronExpressionAdherenceData: `${accordionItemAdherenceData}:cron-expression`,
  inputLookBackPeriodAdherenceData: `${accordionItemAdherenceData}:look-back-period`,
  noticeAdherenceLookBackWarning: `${myIntegrationsScene}:${accordionItemAdherenceData}:notice-look-back-warning`,

  /**
   * Real Time Data task
   */
  accordionItemRealTimeData: `${accordionItemRealTimeData}:accordion-item`,
  checkboxRealTimeDataEnabled: `${myIntegrationsScene}:${accordionItemRealTimeData}:enabled`,

  /**
   * Media import task
   */
  accordionItemMediaImport: `${accordionItemMediaImport}:accordion-item`,
  checkboxMediaImportEnabled: `${myIntegrationsScene}:${accordionItemMediaImport}:enabled`,
  mediaImportAzureConnectionString: (index: number) =>
    `${myIntegrationsScene}:${accordionItemMediaImport}:azure-connection-string:${index}`,
  mediaImportAzureConnectionStringDeleteButton: (index: number) =>
    `${myIntegrationsScene}:${accordionItemMediaImport}:azure-connection-string:${index}:delete`,
  mediaImportAzureConnectionAddButton: `${myIntegrationsScene}:${accordionItemMediaImport}:azure-connection-string:add`,
  /**
   * Test IDs for BlockingWarningModal
   */
  buttonModalAccept: 'accept-blocking-modal',
  buttonModalCancel: 'cancel-blocking-modal',

  /**
   * Test IDs for navigation items
   */
  myIntegrationsNavItem: `${navItem}:${myIntegrationsScene}`,
  workforceManagementNavItem: `${navItem}:workforce-management`,
  qualityManagementNavItem: `${navItem}:quality-management`,
  userManagementNavItem: `${navItem}:user-management`,
  manageAccountNavItem: `${navItem}:manage-account`,
  logoutNavItem: `${navItem}:logout`,

  /**
   * Test IDs for navigation
   */
  navigationHeaderTitle: `${navigation}:header:title`,
  buttonUserManagement: 'user-management-trigger',
  buttonAppSwitcher: 'app-switcher-trigger',
  buttonNavigationToggle: 'nav-toggle-drawer',
} as const;
